import React, { useEffect, useState, useRef, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Box, Button } from "@mui/material";
import { ArrowForwardSharp } from "@mui/icons-material";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

import Logo from "../../shared/Logo/Logo";
import Board from "../ClueBoard/Board/Board";
import SignUpForm from "../../UI/SignUpForm/SignUpForm";
import ClueDetails from "../../UI/ClueDetails/ClueDetails";
import SpeakingIcon from "../../UI/SpeakingIcon/SpeakingIcon";
import HeaderBarcode from "../../shared/HeaderBarcode/HeaderBarcode";
import FooterBarcode from "../../shared/FooterBarcode/FooterBarcode";
import SkipNarrationBtn from "../../UI/SkipNarrationBtn/SkipNarrationBtn";

import usePreloadAssets from "../../../hooks/usePreloadAssets";
import useNarrationsNew from "../../../hooks/useNarrationsNew"; // NEW: individual files

import { addToast } from "../../../redux/slices/toastSlice";
import {
  montageComplete,
  submitDetails,
} from "../../../redux/slices/exitFlowSlice";

import {
  checkIsRevealReady,
  revealCompleteCall,
  rewardCall,
} from "../../../api/ExitApis/initiateExitApi";

import bg from "../../../assets/images/map-bg-dstl-upd-11.webp";

import "./GameFinishP2.scss";

export const getResumeToken = () => {
  try {
    const token = localStorage.getItem("EXIT_RESUME_TOKEN");
    return token || null;
  } catch {
    return null;
  }
};

const Assets = [bg]; // Clue narrations now load on-demand as individual files

const GameFinishP2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const introGameFinish2StartedRef = useRef(false);
  const { systemName, authToken } = useSelector(
    (state) => state.createUser.user,
    shallowEqual
  );
  const { voiceNarration } = useSelector((state) => state.settings);
  //  read the current exit state from Redux to decide what to show on mount
  const exitState = useSelector((s) => s.exitFlow?.state);
  const narrationPlayedRef = useRef(false);
  const isInitialMount = useRef(true);
  // Form and UI state
  const [sending, setSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  // Clue investigation state
  const [flashClues, setFlashClues] = useState(true);
  const [selectedClueId, setSelectedClueId] = useState(null);
  const [showClueDetails, setShowClueDetails] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const [isShowSecondBoard, setIsShowSecondBoard] = useState(false);

  // Final conclusion state
  const [showConclusion, setShowConclusion] = useState(false);

  usePreloadAssets(Assets);
  // Narration hooks
  const introNarration = useNarrationsNew({
    storageKey: "game_finish_p2",
    playOnce: true,
  });

  useEffect(() => {
    if (!voiceNarration || narrationPlayedRef.current) return;
    if (introGameFinish2StartedRef.current) return;
    if (!introNarration.hasPlayed()) {
      introGameFinish2StartedRef.current = true;
      introNarration.playSequence(["game_finish_p2"], 0);
    }
    return () => {
      introNarration.stop();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceNarration]);

  useEffect(() => {
    // Only run on initial mount/page refresh
    if (!isInitialMount.current) return;

    isInitialMount.current = false;

    if (exitState === "montage_done") {
      setShowSignUpForm(true);
      setFlashClues(false);
    }
    if (exitState === "details_submitted") {
      setShowSignUpForm(false);
      setShowClueDetails(true);

      setIsShowSecondBoard(false);
    }
    if (exitState === "reveal_ready") {
      setShowSignUpForm(false);
      setShowClueDetails(true);

      setIsShowSecondBoard(false);
    }
    // if (exitState === "reveal_done" || exitState === "reward_ready") {
    //   navigate("/finals");
    // }
  }, [exitState, navigate]);

  // Proceed: if still at 'initiated', mark montage complete first; otherwise just show the form
  const handleProceed = async () => {
    introNarration.stop();
    if (exitState !== "initiated") {
      // montage already done or further → just show the form
      setShowSignUpForm(true);
      setFlashClues(false);
      return;
    }
    try {
      setSending(true);
      await dispatch(montageComplete({ systemName, authToken })).unwrap();
      setShowSignUpForm(true);
      setFlashClues(false);
    } catch (e) {
      dispatch(
        addToast({
          message: e?.message || "Could not mark montage as complete.",
          isSuccess: false,
        })
      );
    } finally {
      setSending(false);
    }
  };

  const handleFormSubmit = async (signUpForm) => {
    try {
      await dispatch(
        submitDetails({
          systemName,
          authToken,
          name: signUpForm?.fullName,
          email: signUpForm?.email,
          linkedin: signUpForm?.linkedinProfile,
        })
      ).unwrap();

      // success → continue your flow
      setIsSubmitted(true);
      setTimeout(() => {
        setShowSignUpForm(false);
        setIsShowSecondBoard(true);
      }, 4000);

      await checkIsRevealReady(systemName, authToken, getResumeToken());

      dispatch(
        addToast({
          message: "Details received. Reveal phase unlocked!",
          isSuccess: true,
        })
      );
    } catch (e) {
      dispatch(
        addToast({
          message: e?.message || "Could not submit details. Please try again.",
          isSuccess: false,
        })
      );
    }
  };

  useEffect(() => {
    if (isSubmitted && !isShowSecondBoard) {
      setTimeout(() => {
        setShowClueDetails(true);

        setIsShowSecondBoard(false);
      }, 6000);
    }
  }, [isSubmitted, isShowSecondBoard, selectedClueId]);

  const handleClueChange = (arg) => {
    if (typeof arg === "object" && arg !== null) {
      const { missionId, clueId } = arg;
      if (missionId) setSelectedMissionId(missionId);
      if (clueId) setSelectedClueId(clueId);
    } else {
      setSelectedClueId(String(arg));
    }
  };

  const handleInvestigationComplete = async () => {
    try {
      const result = await revealCompleteCall(
        systemName,
        authToken,
        getResumeToken()
      );

      if (result?.state === "reveal_done") {
        navigate("/finals");
        rewardCall(systemName, authToken, getResumeToken());
        setIsShowSecondBoard(false);
      }

      setShowConclusion(true);
      setShowClueDetails(false);
    } catch (e) {
      dispatch(
        addToast({
          message: e?.message || "Could not finalize reveal.",
          isSuccess: false,
        })
      );
    }
  };

  const handleSkipNarration = () => {
    try {
      introNarration.skip();
      introNarration.stop();
    } catch (e) {
      console.warn("introNarration.skip() failed:", e);
    }
  };

  return (
    <Stack className="game-finish-p2-container">
      <Stack className="dotted-background" style={{ zIndex: 4 }}>
        <Stack direction="column" className="container header">
          <HeaderBarcode />
          {(introNarration.isPlaying || isSpeaking) && <SpeakingIcon />}
          {introNarration.isPlaying && (
            <SkipNarrationBtn onSkip={handleSkipNarration} />
          )}
        </Stack>

        <Stack className="container content">
          {/* Initial section: only show while we're not showing the form or clue flow */}
          {!showSignUpForm && !isSubmitted && !showClueDetails && (
            <Stack className="notice-board-p2" useFlexGap gap={2}>
              <Box className="notice-board">
                <Stack direction="column" spacing={2}>
                  <p>
                    Your investigation has uncovered the truth: the operator
                    behind this unauthorised drone is none other than Alex Fox,
                    also known as ByteFox. But who is ByteFox, and why did they
                    orchestrate this elaborate breach? Let’s dive into the
                    evidence you’ve uncovered to piece together the full story.
                    Before we proceed, we need your real identity. Your skills
                    have proven exceptional—this may just be the beginning of
                    your next mission.
                  </p>
                </Stack>
              </Box>

              <Stack justifyContent="end">
                <Button
                  variant="contained"
                  className={`btn btn-flat  ${
                    sending || introNarration.isPlaying ? "disable" : ""
                  }`}
                  onClick={handleProceed}
                  disabled={sending || introNarration.isPlaying}
                  endIcon={
                    <ArrowForwardSharp
                      size={24}
                      sx={{ color: "white", p: 0 }}
                    />
                  }
                  sx={{
                    animation: "fadeInUp 0.6s ease-out",
                    animationDelay: "0.3s",
                    animationFillMode: "both",
                  }}
                >
                  Proceed
                </Button>
              </Stack>
            </Stack>
          )}

          {isShowSecondBoard && !showClueDetails && (
            <Stack className="notice-board notice-board-p3">
              <Stack direction="column" spacing={2}>
                <h1>Your investigation has been meticulous, Investigator</h1>
                <p>
                  However, not everything was as it seemed. ByteFox is a master
                  of misdirection, planting false trails—what we’ll call
                  ‘decoys’—to keep you guessing and test your critical thinking.
                  These decoys were designed to challenge you, distract you, and
                  sometimes even amuse you. Separating the real clues from the
                  crafted distractions was no small feat.
                  <br />
                  <br />
                  Here’s what you discovered—and how ByteFox’s tricks were
                  intended to throw you off the scent:
                </p>
              </Stack>
            </Stack>
          )}
          {/* Investigation board - visible during clue phase */}
          {(showClueDetails || !showConclusion) && (
            <Suspense fallback={<div>Loading..</div>}>
              <Board
                marginTop="-40"
                isFlashes={flashClues}
                isClickable={false}
                selectedClueId={selectedClueId}
                setSelectedClueId={setSelectedClueId}
                setSelectedMissionId={setSelectedMissionId}
                selectedMissionId={selectedMissionId}
              />
            </Suspense>
          )}

          {/* Clue details panel */}
          {showClueDetails && !showConclusion && (
            <ClueDetails
              autoPlay={true}
              showNavigation={true}
              isSpeaking={isSpeaking}
              setIsSpeaking={setIsSpeaking}
              selectedClueId={selectedClueId}
              onClueChange={handleClueChange}
              selectedMissionId={selectedMissionId}
              onComplete={handleInvestigationComplete}
              setSelectedClueId={setSelectedClueId}
              setSelectedMissionId={setSelectedMissionId}
            />
          )}
        </Stack>

        {/* {showSignUpForm && ( */}
        <SignUpForm
          open={showSignUpForm}
          isSubmitted={isSubmitted}
          setOpen={setShowSignUpForm}
          handleFormSubmit={handleFormSubmit}
        />
        {/* )} */}

        <Stack direction="row" className="container">
          <Logo />
          <FooterBarcode />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default GameFinishP2;
